import { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, CheckCircle } from 'lucide-react';
import { Button } from '@airclou/ui';
import { submitFeedback, getFeedbackStatus, getErrorCode } from '@/services/api';

interface HelpfulWidgetProps {
  articleId: string;
}

type FeedbackState = 'loading' | 'idle' | 'submitting' | 'voted' | 'error';

const STORAGE_KEY_PREFIX = 'kb:feedback:';

function getStoredVote(articleId: string): boolean | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${articleId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed.helpful === 'boolean' ? parsed.helpful : null;
  } catch {
    return null;
  }
}

function storeVote(articleId: string, helpful: boolean) {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${articleId}`,
      JSON.stringify({ helpful, timestamp: Date.now() })
    );
  } catch {
    // localStorage unavailable — silent fail
  }
}

export function HelpfulWidget({ articleId }: HelpfulWidgetProps) {
  const [state, setState] = useState<FeedbackState>('loading');
  const [helpful, setHelpful] = useState<boolean | null>(null);

  // On mount: check localStorage first for instant UI, then confirm with server
  useEffect(() => {
    const stored = getStoredVote(articleId);
    if (stored !== null) {
      setHelpful(stored);
      setState('voted');
    }

    getFeedbackStatus(articleId)
      .then((status) => {
        if (status.voted && status.helpful !== undefined) {
          setHelpful(status.helpful);
          storeVote(articleId, status.helpful);
          setState('voted');
        } else if (stored === null) {
          // Only go to idle if localStorage didn't already show voted
          setState('idle');
        }
      })
      .catch(() => {
        // If server check fails, fall back to localStorage result
        if (stored === null) {
          setState('idle');
        }
      });
  }, [articleId]);

  const handleFeedback = async (isHelpful: boolean) => {
    setState('submitting');
    setHelpful(isHelpful);

    try {
      await submitFeedback({ article_id: articleId, helpful: isHelpful });
      storeVote(articleId, isHelpful);
      setState('voted');
    } catch (error) {
      const code = getErrorCode(error);
      if (code === 'RATE_LIMITED') {
        setState('error');
      } else {
        setState('error');
      }
    }
  };

  if (state === 'loading') {
    return (
      <div className="p-4 rounded-lg bg-muted/50">
        <div className="h-5 w-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (state === 'voted') {
    return (
      <div className="p-4 rounded-lg bg-muted/50">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-sm">Thanks for your feedback!</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={helpful === true ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFeedback(true)}
            className="gap-1"
          >
            <ThumbsUp className="h-4 w-4" />
            Yes
          </Button>
          <Button
            variant={helpful === false ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFeedback(false)}
            className="gap-1"
          >
            <ThumbsDown className="h-4 w-4" />
            No
          </Button>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="p-4 rounded-lg bg-destructive/10 text-destructive">
        <p className="text-sm">Something went wrong.</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setState(helpful !== null ? 'voted' : 'idle')}
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg bg-muted/50">
      <p className="text-sm font-medium mb-3">Was this article helpful?</p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleFeedback(true)}
          disabled={state === 'submitting'}
          className="gap-1"
        >
          <ThumbsUp className="h-4 w-4" />
          Yes
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleFeedback(false)}
          disabled={state === 'submitting'}
          className="gap-1"
        >
          <ThumbsDown className="h-4 w-4" />
          No
        </Button>
      </div>
    </div>
  );
}
